import { Card, CardContent, Skeleton } from '@billda/design-system';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useGetWeeklyAllocationTrend } from '../hooks/useGetWeeklyAllocationTrend';
import { formatCurrencyWhole } from '../../../shared/utils/currency';
import type { WeeklyAllocationDataPoint } from '../api/dashboardApi';

const SERIES = {
  booked: { color: '#2196F3', label: 'Booked' },
  approved: { color: '#4CAF50', label: 'Approved' },
  rejected: { color: '#F44336', label: 'Rejected' },
} as const;

const MIN_DATA_POINTS = 3;

function formatYAxis(value: number): string {
  if (value >= 1000) {
    return `£${(value / 1000).toFixed(0)}k`;
  }
  return `£${value}`;
}

/**
 * When there are fewer than MIN_DATA_POINTS weeks of real data,
 * pad the beginning with zeroed-out entries so the chart renders
 * a meaningful area shape rather than a single dot.
 */
function padWeeks(
  weeks: WeeklyAllocationDataPoint[]
): WeeklyAllocationDataPoint[] {
  if (weeks.length >= MIN_DATA_POINTS) {
    return weeks;
  }

  const padding: WeeklyAllocationDataPoint[] = [];
  const needed = MIN_DATA_POINTS - weeks.length;
  const firstWeek = weeks[0];

  for (let i = needed; i > 0; i--) {
    let weekNum = (firstWeek?.weekNumber ?? 1) - i;
    let year = firstWeek?.year ?? 2026;
    if (weekNum < 1) {
      weekNum += 52;
      year -= 1;
    }
    padding.push({
      year,
      weekNumber: weekNum,
      label: `Wk ${weekNum}`,
      booked: 0,
      approved: 0,
      rejected: 0,
    });
  }

  return [...padding, ...weeks];
}

export function WeeklyAllocationTrend() {
  const { data, isLoading } = useGetWeeklyAllocationTrend();

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  const rawWeeks = data?.weeks ?? [];
  const weeks = padWeeks(rawWeeks);
  const displayedWeekCount = weeks.length;

  return (
    <Card data-testid="weekly-allocation-trend" className="min-w-0 flex-1">
      <CardContent className="p-6">
        <div className="mb-6">
          <p className="type-body-lg font-bold">Weekly Allocation Trend</p>
          <p className="mt-0.5 type-body-md text-content-secondary">
            {displayedWeekCount === 0
              ? 'No data yet'
              : `Last ${displayedWeekCount} ${displayedWeekCount === 1 ? 'week' : 'weeks'}`}
          </p>
        </div>

        <div
          className="h-[250px] w-full sm:h-[300px]"
          data-testid="weekly-allocation-trend-chart"
        >
          {weeks.length > 0 ? (
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={0}
              minHeight={0}
              initialDimension={{ width: 600, height: 300 }}
            >
              <AreaChart
                data={weeks}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <defs>
                  {Object.entries(SERIES).map(([key, { color }]) => (
                    <linearGradient
                      key={key}
                      id={`gradient-${key}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                      <stop
                        offset="100%"
                        stopColor={color}
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.08)"
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={formatYAxis}
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={50}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(30, 30, 30, 0.95)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                  formatter={value => formatCurrencyWhole(Number(value))}
                  labelStyle={{
                    color: 'rgba(255,255,255,0.7)',
                    marginBottom: 4,
                  }}
                />
                <Legend
                  verticalAlign="bottom"
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
                />
                {Object.entries(SERIES).map(([key, { color, label }]) => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={label}
                    stroke={color}
                    strokeWidth={2}
                    fill={`url(#gradient-${key})`}
                    dot={{ r: 3, fill: color, strokeWidth: 0 }}
                    activeDot={{ r: 5, strokeWidth: 0 }}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="type-body-md text-content-secondary">
                No allocation data available
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <Card
      data-testid="weekly-allocation-trend-loading"
      className="min-w-0 flex-1"
    >
      <CardContent className="p-6">
        <Skeleton width={200} height={24} className="mb-0.5" />
        <Skeleton width={120} height={18} className="mb-6" />
        <Skeleton variant="rectangular" height={300} className="rounded-lg" />
      </CardContent>
    </Card>
  );
}
